/**
 * Copyright © 2025 Indisea Software LLC. All rights reserved.
 *
 * Proprietary and confidential. Licensed, not sold. Use is subject to the
 * license agreement between Indisea Software LLC and the licensee.
 * See LICENSE for full terms.
 *
 * SPDX-License-Identifier: LicenseRef-Indisea-Proprietary
 */

import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Modal, Button, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Grid, html, h } from 'gridjs';
import 'gridjs/dist/theme/mermaid.css';
import api from '../api';

function CustomersListPage() {
    const [customers, setCustomers] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editFormData, setEditFormData] = useState(null);
    const [editSubmitting, setEditSubmitting] = useState(false);
    const [editError, setEditError] = useState('');
    const [changePassword, setChangePassword] = useState(false);
    const [originalPassword, setOriginalPassword] = useState('');
    const [formData, setFormData] = useState({
        username: '',
        password: '',
        hmacKey: '',
        organizationCode: '',
        disableTaxCalc: false,
        environment: '',
        storeName: '',
        coupaUserId: '',
        coupaPassword: ''
    });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const gridRef = useRef(null);
    const gridWrapperRef = useRef(null);

    const fetchCustomers = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await api.get('/api/Customer/all', {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setCustomers(response.data.items || response.data || []);
        } catch (error) {
            setCustomers([]);
            console.error('Failed to fetch customers:', error);
        }
    }, []);

    useEffect(() => {
        fetchCustomers();
    }, [fetchCustomers]);

    const handleShowModal = useCallback(() => {
        setShowModal(true);
        setError('');
    }, []);

    const handleCloseModal = useCallback(() => {
        setShowModal(false);
        setFormData({
            username: '',
            password: '',
            hmacKey: '',
            organizationCode: '',
            disableTaxCalc: false,
            environment: '',
            storeName: '',
            coupaUserId: '',
            coupaPassword: ''
        });
        setError('');
    }, []);

    const handleChange = useCallback((e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    }, []);

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError('');
        try {
            const token = localStorage.getItem('token');
            await api.post('/api/Customer', formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            await fetchCustomers();  // fetch first
            setShowModal(false);     // close modal after
        } catch (err) {
            setError('Failed to add customer.');
        } finally {
            setSubmitting(false);
        }
    }, [formData, fetchCustomers]);

    const handleViewEdit = async (username, storeName) => {
        setEditError('');
        setEditFormData(null);
        setChangePassword(false);
        setOriginalPassword('');
        setShowEditModal(true);
        try {
            const token = localStorage.getItem('token');
            const response = await api.post('/api/Customer/GetCustomer', {
                username,
                storeName
            }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setEditFormData(response.data);
            setOriginalPassword(response.data.password || '');
        } catch (err) {
            setEditError('Failed to fetch customer details.');
        }
    };

    const headerBtnHandler = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        handleShowModal();
    }, [handleShowModal]);

    useEffect(() => {
        if (!gridWrapperRef.current) return;

        // Clear previous content
        gridWrapperRef.current.innerHTML = '';

        // Prevent double render
        if (gridRef.current) {
            gridRef.current.destroy();
            gridRef.current = null;
        }

        // Prepare data rows
        const rows = customers.map((customer) => [
            customer.username ?? '',
            customer.organizationCode ?? '',
            customer.storeName ?? '',
            customer.disableTaxCalc ? 'No' : 'Yes',
        ]);

        // Setup Grid
        const grid = new Grid({
            columns: [
                { id: 'username', name: 'Username', sort: true },
                { id: 'organizationCode', name: 'Organization Code', sort: true },
                { id: 'storeName', name: 'Coupa Store Name', sort: true },
                { id: 'active', name: 'Active', sort: true },
                {
                    id: 'logs',
                    name: html(`
                    <div style="display: flex; justify-content: center; align-items: center; width: 100%;">
                      <button class="btn btn-primary btn-sm" title='Add Customer' id="add-customer-header-btn">
                        <i class="fa-solid fa-plus"></i>
                      </button>
                    </div>
                `),
                    sort: false,
                    width: '120px',
                    formatter: (_, row) => {
                        const username = String(row.cells[0]?.data ?? '');
                        const storeName = String(row.cells[2]?.data ?? '');
                        return h('div', { className: 'd-flex gap-1 justify-content-end' }, [
                            h('button', {
                                className: 'btn btn-sm btn-primary',
                                onclick: () => {
                                    const encodedUser = encodeURIComponent(username);
                                    const encodedStore = encodeURIComponent(storeName);
                                    window.location.href = `/logs?user=${encodedUser}&store=${encodedStore}`;
                                },
                                title: 'View Logs'
                            }, h('i', { className: 'fa-solid fa-eye' })),
                            h('button', {
                                className: 'btn btn-sm btn-primary',
                                onclick: () => handleViewEdit(username, storeName),
                                title: 'Edit'
                            }, h('i', { className: 'fa-solid fa-pen-to-square' }))
                        ]);
                    }
                }
            ],
            data: rows,
            pagination: { limit: 10 },
            search: true,
        });

        grid.render(gridWrapperRef.current);
        gridRef.current = grid;

        // Hook up the header Add button
        const timer = setTimeout(() => {
            const btn = document.getElementById('add-customer-header-btn');
            if (btn) {
                btn.addEventListener('click', headerBtnHandler);
            }
        }, 100);

        return () => {
            clearTimeout(timer);
            if (gridWrapperRef.current) {
                gridWrapperRef.current.innerHTML = '';
            }
            if (gridRef.current) {
                gridRef.current.destroy();
                gridRef.current = null;
            }
        };
    }, [customers]);


    useEffect(() => {
        let btn;
        let editBtns = [];
        const timer = setTimeout(() => {
            btn = document.getElementById('add-customer-header-btn');
            if (btn) {
                btn.innerHTML = '';
                const iconContainer = document.createElement('span');
                iconContainer.innerHTML = `<svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="plus" class="svg-inline--fa fa-plus fa-w-14" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" style="width:1em;height:1em;"><path fill="currentColor" d="M432 256c0 13.3-10.7 24-24 24h-152v152c0 13.3-10.7 24-24 24s-24-10.7-24-24V280H56c-13.3 0-24-10.7-24-24s10.7-24 24-24h152V80c0-13.3 10.7-24 24-24s24 10.7 24 24v152h152c13.3 0 24 10.7 24 24z"></path></svg>`;
                btn.appendChild(iconContainer);
                btn.addEventListener('click', headerBtnHandler);
            }

            editBtns = Array.from(document.getElementsByClassName('view-edit-btn'));
            editBtns.forEach(editBtn => {
                editBtn.addEventListener('click', handleViewEditClick);
            });
        }, 50);
        return () => {
            clearTimeout(timer);
            if (btn) btn.removeEventListener('click', headerBtnHandler);
            if (editBtns.length > 0) {
                editBtns.forEach(editBtn => {
                    editBtn.removeEventListener('click', handleViewEditClick);
                });
            }
        };
    }, [customers, headerBtnHandler]);

    const handleViewEditClick = useCallback(async (e) => {
        e.preventDefault();
        e.stopPropagation();
        let btn = e.currentTarget;
        if (!btn || !btn.dataset) {
            btn = e.target.closest('.view-edit-btn');
        }
        if (!btn) return;

        const username = decodeURIComponent(btn.dataset.username || '');
        const storeName = decodeURIComponent(btn.dataset.storename || '');
        setEditError('');
        setEditFormData(null);
        setChangePassword(false);
        setOriginalPassword('');
        setShowEditModal(true);
        try {
            const token = localStorage.getItem('token');
            const response = await api.post('/api/Customer/GetCustomer', {
                username,
                storeName
            }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setEditFormData(response.data);
            setOriginalPassword(response.data.password || '');
        } catch (err) {
            setEditError('Failed to fetch customer details.');
        }
    }, []);

    return (
        <div className="container">
            <h2 className="">Customers List</h2>
            <hr />
            <div id="grid-wrapper" ref={gridWrapperRef}></div>

            {/* View/Edit Modal */}
            <Modal show={showEditModal} onHide={() => { setShowEditModal(false); setChangePassword(false); setOriginalPassword(''); }}>
                <Modal.Header closeButton>
                    <Modal.Title>View/Edit Customer</Modal.Title>
                </Modal.Header>
                <Form
                    onSubmit={async (e) => {
                        e.preventDefault();
                        setEditSubmitting(true);
                        setEditError('');
                        try {
                            const token = localStorage.getItem('token');
                            const reqBody = {
                                username: editFormData.username,
                                password: changePassword ? editFormData.password : originalPassword,
                                hmacKey: editFormData.hmacKey,
                                organizationCode: editFormData.organizationCode,
                                disableTaxCalc: editFormData.disableTaxCalc,
                                storeName: editFormData.storeName,
                                coupaUserId: editFormData.coupaUserId,
                                coupaPassword: editFormData.coupaPassword
                            };
                            await api.post('/api/Customer/UpdateCustomer', reqBody, {
                                headers: {
                                    Authorization: `Bearer ${token}`,
                                },
                            });
                            await fetchCustomers(); // fetch first
                            setShowEditModal(false); // close after
                            setChangePassword(false);
                            setOriginalPassword('');
                        } catch (err) {
                            setEditError('Failed to update customer.');
                        } finally {
                            setEditSubmitting(false);
                        }
                    }}
                >
                    <Modal.Body>
                        {editError && <div className="alert alert-danger">{editError}</div>}
                        {!editFormData ? (
                            <div>Loading...</div>
                        ) : (
                            <>
                                <Form.Group className="mb-2">
                                    <Form.Label>Username</Form.Label>
                                    <Form.Control name="username" value={editFormData.username} onChange={e => setEditFormData(f => ({ ...f, username: e.target.value }))} required />
                                </Form.Group>
                                <Form.Group className="mb-2">
                                    <div className="d-flex align-items-center mb-1">
                                        <Form.Check 
                                            type="checkbox" 
                                            label="Change Password" 
                                            name="changePassword" 
                                            checked={changePassword} 
                                            onChange={e => {
                                                const checked = e.target.checked;
                                                setChangePassword(checked);
                                                if (checked) {
                                                    setEditFormData(f => ({ ...f, password: '' }));
                                                } else {
                                                    setEditFormData(f => ({ ...f, password: originalPassword }));
                                                }
                                            }} 
                                            className="me-2"
                                        />
                                        <OverlayTrigger
                                            placement="top"
                                            overlay={
                                                <Tooltip id="password-tooltip">
                                                    Check this box to change the password. When enabled, you can enter a new password. When disabled, the existing password will be preserved.
                                                </Tooltip>
                                            }
                                        >
                                            <i className="fa-solid fa-circle-info text-info" style={{ cursor: 'help' }}></i>
                                        </OverlayTrigger>
                                    </div>
                                    <Form.Control 
                                        name="password" 
                                        type="password" 
                                        value={changePassword ? editFormData.password : '••••••••'} 
                                        onChange={e => setEditFormData(f => ({ ...f, password: e.target.value }))} 
                                        disabled={!changePassword}
                                        placeholder={changePassword ? 'Enter new password' : '••••••••'}
                                        required={changePassword}
                                    />
                                </Form.Group>
                                <Form.Group className="mb-2">
                                    <Form.Label>HMAC Key</Form.Label>
                                    <Form.Control name="hmacKey" value={editFormData.hmacKey} onChange={e => setEditFormData(f => ({ ...f, hmacKey: e.target.value }))} required />
                                </Form.Group>
                                <Form.Group className="mb-2">
                                    <Form.Label>Organization Code</Form.Label>
                                    <Form.Control name="organizationCode" value={editFormData.organizationCode} onChange={e => setEditFormData(f => ({ ...f, organizationCode: e.target.value }))} required />
                                </Form.Group>
                                <Form.Group className="mb-2">
                                    <Form.Check type="checkbox" label="Active" name="disableTaxCalc" checked={!editFormData.disableTaxCalc} onChange={e => setEditFormData(f => ({ ...f, disableTaxCalc: !e.target.checked }))} />
                                </Form.Group>
                                <Form.Group className="mb-2">
                                    <Form.Label>Coupa Store Name</Form.Label>
                                    <Form.Control name="storeName" value={editFormData.storeName} onChange={e => setEditFormData(f => ({ ...f, storeName: e.target.value }))} required />
                                </Form.Group>
                                <Form.Group className="mb-2">
                                    <Form.Label>Coupa User ID</Form.Label>
                                    <Form.Control name="coupaUserId" value={editFormData.coupaUserId} disabled readOnly />
                                </Form.Group>
                                <Form.Group className="mb-2">
                                    <Form.Label>Coupa Password</Form.Label>
                                    <Form.Control name="coupaPassword" value={editFormData.coupaPassword} disabled readOnly />
                                </Form.Group>
                            </>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowEditModal(false)} disabled={editSubmitting}>Cancel</Button>
                        <Button variant="primary" type="submit" disabled={editSubmitting || !editFormData}>{editSubmitting ? 'Saving...' : 'Save Changes'}</Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Add Customer Modal */}
            <Modal show={showModal} onHide={handleCloseModal}>
                <Modal.Header closeButton>
                    <Modal.Title>Add Customer</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        {error && <div className="alert alert-danger">{error}</div>}
                        <Form.Group className="mb-2">
                            <Form.Label>Username</Form.Label>
                            <Form.Control name="username" value={formData.username} onChange={handleChange} required />
                        </Form.Group>
                        <Form.Group className="mb-2">
                            <Form.Label>Password</Form.Label>
                            <Form.Control name="password" type="password" value={formData.password} onChange={handleChange} required />
                        </Form.Group>
                        <Form.Group className="mb-2">
                            <Form.Label>HMAC Key</Form.Label>
                            <Form.Control name="hmacKey" value={formData.hmacKey} onChange={handleChange} required />
                        </Form.Group>
                        <Form.Group className="mb-2">
                            <Form.Label>Organization Code</Form.Label>
                            <Form.Control name="organizationCode" value={formData.organizationCode} onChange={handleChange} required />
                        </Form.Group>
                        <Form.Group className="mb-2">
                            <Form.Label>Coupa Store Name</Form.Label>
                            <Form.Control name="storeName" value={formData.storeName} onChange={handleChange} required />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseModal} disabled={submitting}>Cancel</Button>
                        <Button variant="primary" type="submit" disabled={submitting}>{submitting ? 'Adding...' : 'Add Customer'}</Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
}

export default CustomersListPage;
